import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import CryptoTradeMode from "./CryptoTradeMode";

/* ===== Crypto — paper trader on Shark Exchange perpetuals =====
   Index-tab machinery applied to crypto: daily trend-pullback entries,
   SL / 20-day-low structural trail (tier + 10R target retired 29 Sep 2026).
   VALIDATION MODE sizing: fixed risk per trade (qty = risk / SL-distance,
   every trade = 1R) — the crypto analogue of the index tabs' fixed lots.
   No budget constraints; SL/fees/funding fully modeled. Paper only.
   Layout mirrors the Indian index tabs: ONE trade table (open + closed),
   with Capital (notional ₹) and Risk (1R ₹) columns. */

const rup = (v) => v == null ? "—" : `${v >= 0 ? "+" : "-"}₹${Math.abs(Math.round(v)).toLocaleString("en-IN")}`;
const inr = (v) => v == null ? "—" : `₹${Math.round(v).toLocaleString("en-IN")}`;
const isUsdt = (sym) => String(sym || "").endsWith("USDT");
const px = (v, sym) => v == null ? "—" : `${isUsdt(sym) ? "$" : "₹"}${Number(v).toLocaleString(isUsdt(sym) ? "en-US" : "en-IN", { maximumFractionDigits: v > 1000 ? 0 : 2 })}`;
const when = (v) => v ? new Date(v).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }) : "-";

export default function CryptoTab() {
  const [st, setSt] = useState(null);
  const [err, setErr] = useState("");
  const [cfg, setCfg] = useState(null);   // editable copy
  const [cfgMsg, setCfgMsg] = useState("");
  const [detail, setDetail] = useState(null); // drawer trade
  const [fSym, setFSym] = useState("ALL");    // instrument filter
  const [fRes, setFRes] = useState("ALL");    // result filter: ALL/OPEN/PROFIT/LOSS
  const [fScope, setFScope] = useState("NOW"); // NOW = current trading plan only (default) / ALL = full history

  const load = useCallback(async () => {
    try {
      const res = await axios.get("/api/crypto/trader/status", { timeout: 15000 });
      if (res.data.success) {
        setSt(res.data);
        setErr("");
        setCfg(c => c || { ...res.data.config });
      }
    } catch (e) { setErr(e.message); }
  }, []);

  useEffect(() => { load(); const id = setInterval(load, 15000); return () => clearInterval(id); }, [load]);

  const post = async (ep, body) => { await axios.post(`/api/crypto/trader/${ep}`, body).catch(() => {}); load(); };
  const saveCfg = async () => {
    await post("config", cfg);
    setCfgMsg("Saved — applies to new trades");
    setTimeout(() => setCfgMsg(""), 4000);
  };

  if (!st) return <div className="loading"><div className="spinner" /><p>{err || "Loading crypto trader…"}</p></div>;

  const closed = st.closedTrades || [];
  const wins = closed.filter(t => (t.pnl_inr || 0) > 0).length;
  const feeSide = st?.params?.feePerSide ?? 0.000472;
  const fxNow = st?.fx?.rate;
  const kOf = (t) => isUsdt(t.symbol) ? (fxNow || t.fxEntry || 95.5) : 1;
  const chargesOf = (t) => (t.entryFee || 0) + (t.tierFee || 0) + (t.exitFee || 0) + (t.fundingFee || 0);
  const estExitFee = (t) => {   // open trades: the sell-leg fee the eventual exit will cost
    const p = t.lastPrice; if (p == null) return 0;
    return p * (t.remainingQty ?? t.qty) * feeSide * kOf(t);
  };
  const livePnl = (t) => {
    const p = t.lastPrice;
    if (p == null) return null;
    const rem = t.remainingQty ?? t.qty;
    const sign = t.side === "SHORT" ? -1 : 1;
    // bookedPnl is already net of the tier-sell fee; all money fields are INR
    return (t.bookedPnl || 0) + (p - t.entry) * rem * sign * kOf(t) - (t.entryFee || 0) - estExitFee(t) - (t.fundingFee || 0);
  };
  // Indian-tab convention: Net = Gross − Charges. Open rows include the
  // estimated exit fee in Charges so the identity holds live.
  const moneyOf = (t) => {
    if (t.status === "OPEN") {
      const net = livePnl(t);
      const charges = chargesOf(t) + estExitFee(t);
      return { net, charges, gross: net == null ? null : net + charges };
    }
    const charges = chargesOf(t);
    return { net: t.pnl_inr, charges, gross: t.pnl_inr == null ? null : t.pnl_inr + charges };
  };
  const capitalOf = (t) => t.notional || t.entry * t.qty * (t.fxEntry || 1);   // INR notional at entry
  const levOf = (t) => st.contractSpecs?.[t.symbol]?.maxLeverage;              // Shark per-contract max
  const planLev = Math.max(1, Number(cfg?.planLeverage ?? st.config?.planLeverage ?? 10));   // legacy fallback
  const planFrac = Math.min(1, Math.max(0.05, Number(cfg?.planLevFrac ?? st.config?.planLevFrac ?? 0.4)));
  // STOP-DERIVED leverage (user rule, 26 Sep 2026): lev = 1/(1.5 x stop-distance),
  // capped at the contract max -> liquidation always sits >=1.5x beyond the stop,
  // so the stop fires first by construction. Margin ~= 1.5 x risk on most trades.
  const effLevOf = (t) => {
    const cap = capitalOf(t); const mx = levOf(t);
    const ideal = cap / (1.5 * (t.riskInr || 10000));
    return Math.max(1, Math.floor(Math.min(mx || ideal, ideal)));
  };
  const marginOf = (t) => capitalOf(t) / effLevOf(t);                          // actual capital a live trade blocks

  // current lifecycle stage of an OPEN trade (the "where does this trade stand" cell)
  const stageOf = (t) => {
    if (t.status !== "OPEN") return null;
    const sign = t.side === "SHORT" ? -1 : 1;
    if (t.tierDone) { const pct = Math.round(100 * (1 - (t.remainingQty ?? t.qty) / t.qty)); return { icon: "🏦", label: "BE runner", note: `${pct}% banked · stop at entry, risk ₹0` }; }
    const stopLocked = sign * (t.stop - t.entry) > 0;
    if (stopLocked) {
      const locked = sign * (t.stop - t.entry) * (t.remainingQty ?? t.qty) * kOf(t);
      return { icon: "🔒", label: `trailing · locked ${rup(locked)}`, note: "stop beyond entry — worst case is profit" };
    }
    if (t.stArmed) return { icon: "📈", label: "ST trailing", note: "armed — stop ratchets with the ST line" };
    const tierTf = (st.tierTfs || ["1d"]).includes(t.tf);
    return { icon: "⏳", label: "full 1R at risk", note: tierTf ? "20-day-low trail ratchets the stop" : "waiting for ST to arm" };
  };

  const allRows = [...(st.openTrades || []), ...closed]
    .sort((a, b) => new Date(b.entry_time) - new Date(a.entry_time));
  // "Now trading" scope: only the current universe + enabled books (post
  // alt-cut plan). Legacy positions on removed symbols and paused books are
  // history — visible under "All history".
  const universe = st.symbols || [];
  const inPlan = (t) =>
    t.book === "OBX" ? !!(st.config?.obxEnabled ?? 1)
      : t.book === "OBS" ? !!(st.config?.obsEnabled ?? 1)
        : t.book === "OB" ? !!(st.config?.obEnabled) && universe.includes(t.symbol)
          : universe.includes(t.symbol);
  const scopedRows = fScope === "NOW" ? allRows.filter(inPlan) : allRows;
  // chips always include the live universe (greyed when no trades yet), so
  // armed-but-quiet instruments (e.g. XAUUSDT) stay visible in the plan
  const tradedSyms = new Set(scopedRows.map(t => t.symbol));
  const symList = [...new Set([...universe, ...tradedSyms])];
  const effSym = fSym === "ALL" || symList.includes(fSym) ? fSym : "ALL";
  const scopedClosed = scopedRows.filter(t => t.status !== "OPEN");
  const scopedOpen = scopedRows.length - scopedClosed.length;
  const scopedWins = scopedClosed.filter(t => (t.pnl_inr || 0) > 0).length;
  const scopedNet = scopedRows.reduce((a, t) => a + (moneyOf(t).net || 0), 0);
  const rows = scopedRows.filter(t => {
    if (effSym !== "ALL" && t.symbol !== effSym) return false;
    if (fRes === "OPEN") return t.status === "OPEN";
    if (fRes === "PROFIT" || fRes === "LOSS") {
      const { net } = moneyOf(t);
      if (net == null) return false;
      return fRes === "PROFIT" ? net > 0 : net < 0;
    }
    return true;
  });
  const chip = (active) => ({
    fontSize: 11, padding: "3px 10px", borderRadius: 12, cursor: "pointer",
    border: active ? "1px solid var(--accent, #6366f1)" : "1px solid var(--panel3)",
    background: active ? "var(--tint-pos)" : "var(--chart-bg)",
    color: active ? "var(--text2)" : "var(--muted2)", fontWeight: active ? 700 : 500,
  });

  // ---- funding planner: what going live at planLev would actually need ----
  // Sweep the whole forward test: margins of concurrently-open positions.
  const planner = (() => {
    const ev = [];
    allRows.forEach(t => {
      const m = marginOf(t);
      ev.push([Date.parse(t.entry_time), m, t.riskInr || 0]);
      ev.push([t.exit_time ? Date.parse(t.exit_time) : Infinity, -m, -(t.riskInr || 0)]);
    });
    ev.sort((a, b) => a[0] - b[0]);
    let margin = 0, risk = 0, count = 0, peakMargin = 0, peakRisk = 0, peakCount = 0;
    ev.forEach(([, dm, dr]) => {
      margin += dm; risk += dr; count += dm > 0 ? 1 : -1;
      if (margin > peakMargin) { peakMargin = margin; peakRisk = risk; peakCount = count; }
    });
    const openNow = (st.openTrades || []);
    const openMargin = openNow.reduce((s, t) => s + marginOf(t), 0);
    const openRisk = openNow.reduce((s, t) => {
      const sign = t.side === "SHORT" ? -1 : 1;
      const protectedStop = t.tierDone || sign * (t.stop - t.entry) >= 0;
      return s + (protectedStop ? 0 : (t.riskInr || 0));
    }, 0);
    const suggested = peakMargin * 1.1 + peakCount * (st.config?.riskPerTrade || 10000);
    return { openMargin, openRisk, openCount: openNow.length, peakMargin, peakRisk, peakCount, suggested };
  })();

  return (
    <div className="crypto-wide">
      <div className="dashboard-header">
        <div>
          <h2>Crypto <span style={{ fontSize: 12, color: "var(--muted2)", fontWeight: 400 }}>Shark perpetuals · 1d trail + 4h ST-trail + gold 10R + bear shorts · paper</span></h2>
          <p className="dashboard-subtitle">
            Two books per coin: 1d trail (SL → 20-day-low structural trail, full position, no tier, no fixed target — replaced the tier+10R hybrid 29 Sep 2026 after a 5.6y replay showed the +0.65% tier was the bleed and the 10R target never once hit) and 4h Supertrend-trail (structural SL until ST(10,3) turns up, then the stop ratchets with the ST line; flip = exit — no fixed target, ~2 trades/week). Gold (PAXG) runs the 4h book only with the FIXED 10R exit — its smooth trends reach 10R and hate trails (3y: avgR +1.55). A SHORT book (4h, fixed 10R, crypto only) activates only when the DAILY trend is down — the regime where the long books sit idle (+31R in the 2026 bear).
            Same trend-pullback entries, fixed-risk sizing (every trade risks the same ₹ at its SL — validation mode, no budget constraints). Paper only — no orders, no keys.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <CryptoTradeMode />
          {st.active
            ? <button className="btn btn-sm btn-danger" onClick={() => post("stop")}>Stop</button>
            : <button className="btn btn-sm btn-primary" onClick={() => post("start")}>Start</button>}
          {st.closedCount > 0 && <button className="btn btn-sm btn-secondary" onClick={() => post("clear")}>Clear Closed ({st.closedCount})</button>}
        </div>
      </div>

      {st.active && (st.suggestions || []).length === 0 && (
        <div style={{ fontSize: 10.5, color: "var(--faint2)", margin: "8px 0" }}>
          💡 No open trade currently clears the suggestion bar (symbol needs ≥{st.config?.suggestMinSymbolN ?? 5} closes at ≥{st.config?.suggestMinWR ?? 50}% WR with positive R, inside a profitable book) — tickets appear and alert on Telegram as records qualify.
        </div>
      )}
      {(st.suggestions || []).length > 0 && (
        <div style={{ margin: "10px 0" }}>
          <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 1, color: "#f59e0b", fontWeight: 700, marginBottom: 6 }}>
            💡 Suggestions — only entries where the SYMBOL's own record earns it (≥{st.config?.suggestMinSymbolN ?? 5} closes, positive R, ≥{st.config?.suggestMinWR ?? 50}% WR, inside a profitable book) · sized to ₹{st.config?.suggestRiskInr ?? 500} risk · advisory, you execute manually
          </div>
          <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 4 }}>
            {st.suggestions.map(sg => (
              <div key={sg.ref} style={{
                minWidth: 290, background: "var(--chart-bg)", borderRadius: 8, padding: "8px 12px",
                border: `1px solid ${sg.tier === "green" ? "#22c55e" : "#f59e0b"}`,
              }}>
                <div style={{ fontWeight: 700, fontSize: 13 }}>
                  {sg.tier === "green" ? "🟢" : "🟡"} {sg.side === "SHORT" ? "SELL" : "BUY"} {sg.symbol}
                  <span style={{ fontWeight: 400, fontSize: 10, color: "var(--muted2)" }}> {sg.ref} · {sg.book}{sg.tier === "watch" ? " · WATCH (thin symbol history)" : ""}</span>
                </div>
                <div style={{ fontSize: 12, marginTop: 4 }}>
                  entry ≈ <b>{sg.entry}</b>{sg.lastPrice != null && <span style={{ color: "var(--muted2)" }}> (now {sg.lastPrice})</span>} · qty <b>{sg.qty}</b> <span style={{ color: "var(--muted2)" }}>(risk ₹{sg.riskInr}{sg.note ? ` · ${sg.note}` : ""})</span>
                </div>
                <div style={{ fontSize: 12 }}>
                  SL <b className="sl">{sg.stop}</b> · {sg.target ? <>target <b className="positive">{sg.target}</b></> : <span title={sg.targetNote || ""}>target: <b>trail</b> (SL ratchets with the book)</span>}
                </div>
                <div style={{ fontSize: 11, color: "var(--muted2)" }}>lev ≤ {sg.lev}x · margin ≈ ₹{(sg.marginInr || 0).toLocaleString("en-IN")} · notional ₹{(sg.notionalInr || 0).toLocaleString("en-IN")}</div>
                {sg.liqPrice != null && (
                  <div style={{ fontSize: 10.5, color: (sg.liqHeadroomPct ?? 99) < 15 ? "#ef4444" : "var(--muted2)" }}
                    title={`Exact tier-table liquidation at ${sg.lev}x (maintenance ${sg.maintPct}% of initial margin + venue buffer). Computed for the TICKET leverage — if your Shark contract is set higher, liq is closer: add margin on the position or lower the leverage.`}>
                    liq ≈ {sg.liqPrice} · {sg.liqHeadroomPct}% beyond SL{(sg.liqHeadroomPct ?? 99) < 15 ? " ⚠️ THIN — add margin / lower lev" : ""}
                  </div>
                )}
                <div style={{ fontSize: 10, color: "var(--faint2)", marginTop: 3 }}>{sg.why}</div>
                {sg.aiTip && <div style={{ fontSize: 10.5, marginTop: 3, color: sg.aiTip.stance === "HOLD" ? "var(--muted2)" : sg.aiTip.stance === "CAUTION" ? "#f59e0b" : "#ef4444" }}>
                  🤖 {sg.aiTip.stance}: {sg.aiTip.note}</div>}
              </div>
            ))}
          </div>
        </div>
      )}
      {/* status + account strip */}
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center", background: st.active ? "var(--tint-pos)" : "var(--panel3)", border: "1px solid var(--panel3)", borderRadius: 8, padding: "8px 14px", marginBottom: 10, fontSize: 12.5 }}>
        <span>{st.active ? "● TRADING 24/7" : "○ STOPPED"}</span>
        <span title={fScope === "NOW" ? "Scoped to the current trading plan (filter below) — switch to All history for the whole book" : "All trades ever"}>
          Net P&L{fScope === "NOW" ? " (plan)" : ""}: <b className={(fScope === "NOW" ? scopedNet : st.totalPnlInr) >= 0 ? "positive" : "negative"}>{rup(fScope === "NOW" ? scopedNet : st.totalPnlInr)}</b></span>
        <span>Risk unit: ₹{Math.round(st.config?.riskPerTrade || 0).toLocaleString("en-IN")}/trade (1R)</span>
        <span>USDT/INR: <b>{st.fx?.rate ?? "—"}</b></span>
        <span>{fScope === "NOW"
          ? `${scopedOpen} open · ${scopedClosed.length} closed (${scopedWins}W ${scopedClosed.length - scopedWins}L)`
          : `${st.openCount} open · ${st.closedCount} closed (${wins}W ${st.closedCount - wins}L) · 🏦 ${st.bankedCount} banked · 🎯 ${st.fullTargets} full 10R`}</span>
        <span title="Order plumbing for a future live switch — every action is shaped into a Shark webhook payload but NOTHING is sent in dry-run.">
          🪝 webhook: {st.webhook?.dryRun === false ? <b className="negative">LIVE</b> : <b>dry-run</b>}
        </span>
        <span style={{ marginLeft: "auto", fontSize: 10.5, color: "var(--muted2)" }}>last poll {when(st.lastPoll)}{st.lastError ? ` · err: ${st.lastError}` : ""}</span>
      </div>

      {/* regime strip — one chip per symbol */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
        {(st.symbols || []).map(s => {
          const r = st.regime?.[s];
          const on = r?.trendOn;
          const lp = st.livePrices?.[s] ?? r?.price;
          return (
            <span key={s} style={{ fontSize: 11.5, padding: "4px 10px", borderRadius: 14, border: "1px solid var(--panel3)", background: "var(--chart-bg)" }} title={st.livePrices?.[s] != null ? "live (60s)" : "last daily close — live price loading"}>
              <b>{s.replace("INR", "").replace("USDT", "")}</b> {lp != null ? px(lp, s) : "—"}{" "}
              <span className={on ? "positive" : "negative"} style={{ fontWeight: 600 }}>
                {on == null ? (r?.note || "…") : on ? "● trend ON" : r?.trendDown ? "▼ downtrend (shorts armed)" : "○ trend OFF"}
              </span>
            </span>
          );
        })}
        <span style={{ fontSize: 10.5, color: "var(--muted2)", alignSelf: "center" }}>trend = daily EMA50 &gt; EMA200 &amp; close &gt; EMA50 — no entries while OFF</span>
      </div>

      {/* config strip */}
      {cfg && (
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end", background: "var(--chart-bg)", border: "1px solid var(--panel3)", borderRadius: 8, padding: "8px 14px", marginBottom: 12, fontSize: 12 }}>
          {[["riskPerTrade", "Risk ₹ per trade (1R)"], ["funding8hPct", "Funding %/8h"], ["planLevFrac", "Leverage: fraction of max (0.4 = 40%)"]].map(([k, label]) => (
            <label key={k} style={{ color: "var(--muted2)", fontSize: 11 }}>
              {label}
              <input type="number" value={cfg[k]} onChange={e => setCfg({ ...cfg, [k]: e.target.value })}
                style={{ display: "block", width: 110, padding: "4px 8px", marginTop: 2, background: "var(--panel3)", border: "1px solid var(--border2)", borderRadius: 6, color: "var(--text2)", fontSize: 12 }} />
            </label>
          ))}
          <button className="btn btn-sm btn-secondary" onClick={saveCfg}>Save</button>
          {cfgMsg && <span className="positive" style={{ fontSize: 11 }}>{cfgMsg}</span>}
        </div>
      )}

      {/* funding planner — the "how much do I load to go live" strip */}
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center", background: "var(--chart-bg)", border: "1px solid var(--panel3)", borderRadius: 8, padding: "8px 14px", marginBottom: 12, fontSize: 12 }}>
        <span style={{ fontWeight: 700 }}>💰 Funding planner @ stop-derived leverage</span>
        <span>Open now: <b>{planner.openCount}</b> positions · margin <b>{inr(planner.openMargin)}</b> · at risk <b className={planner.openRisk > 0 ? "negative" : "positive"}>{inr(planner.openRisk)}</b></span>
        <span>Peak of the whole test: <b>{planner.peakCount}</b> concurrent · margin <b>{inr(planner.peakMargin)}</b></span>
        <span title="peak margin + 10% buffer + 1R stop room for each concurrently open trade">Suggested float at THIS size: <b className="positive">{inr(planner.suggested)}</b></span>
        <span style={{ color: "var(--muted2)" }} title="risk scaled to ₹1,000/trade — the proposed cautious live start">at 0.1× live scale: <b>{inr(planner.suggested / 10)}</b></span>
        <span style={{ fontSize: 10, color: "var(--muted2)", flexBasis: "100%" }}>
          Margin = notional ÷ stop-derived leverage, where leverage = 1 ÷ (1.5 × stop distance), capped at the contract max — liquidation always sits ≥1.5× beyond the stop, so the stop-loss fires first by construction (≈ ₹15k margin per ₹10k-risk trade). Suggested = peak margin ×1.1 + one full stop (1R) per concurrently open trade.
        </span>
      </div>

      <h4 style={{ margin: "10px 0 6px" }}>Trades ({rows.length}{rows.length !== allRows.length ? ` of ${allRows.length}` : ""}) <span style={{ fontSize: 10.5, color: "var(--muted2)", fontWeight: 400 }}>open + closed, newest first — click a row for the full money breakdown</span></h4>

      {/* filters: scope (default = current plan) + instrument + result */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
        <span style={{ fontSize: 10.5, color: "var(--muted2)" }}>Show:</span>
        <button style={chip(fScope === "NOW")} onClick={() => setFScope("NOW")}
          title="Only what we trade NOW: the current universe + enabled books. Legacy alt positions and paused books are hidden.">
          🎯 Now trading</button>
        <button style={chip(fScope === "ALL")} onClick={() => setFScope("ALL")}
          title="Every trade ever, including retired symbols and paused books.">All history</button>
        <span style={{ fontSize: 10.5, color: "var(--muted2)", marginLeft: 10 }}>Instrument:</span>
        {["ALL", ...symList].map(s => {
          const quiet = s !== "ALL" && !tradedSyms.has(s);
          return (
            <button key={s} style={{ ...chip(effSym === s), ...(quiet ? { opacity: 0.5 } : {}) }}
              title={quiet ? "In the trading plan — no trades yet (books armed, waiting on entry conditions)" : undefined}
              onClick={() => setFSym(s)}>{s === "ALL" ? "All" : s}</button>
          );
        })}
        <span style={{ fontSize: 10.5, color: "var(--muted2)", marginLeft: 10 }}>Result:</span>
        {[["ALL", "All"], ["OPEN", "Open"], ["PROFIT", "🟢 Profit"], ["LOSS", "🔴 Loss"]].map(([v, l]) => (
          <button key={v} style={chip(fRes === v)} onClick={() => setFRes(v)}>{l}</button>
        ))}
      </div>

      <div className="stocks-table-wrapper tl-table-wrap">
        <table className="stocks-table" style={{ width: "100%" }}>
          <thead><tr>
            <th>Time</th><th>Exit</th><th>Symbol</th><th>Book</th><th>Type</th><th>Qty</th>
            <th>Entry</th><th>SL</th><th>Target</th><th>Exit Px</th>
            <th title="Current market price of the instrument (live, 60s) — reference on every row, including closed trades.">CMP</th>
            <th title="Notional position value in ₹ at entry (qty × price) — NOT money paid; perpetuals block only margin = this ÷ your leverage setting.">Capital (notional)</th>
            <th title="Actual capital a live trade blocks at STOP-DERIVED leverage: lev = 1 ÷ (1.5 × stop distance), capped at the contract max — guarantees the stop-loss triggers before liquidation.">Margin (stop-safe)</th>
            <th title="₹ lost if the stop is hit — every trade is sized to the same risk (1R).">Risk (1R)</th>
            <th>Gross P&L</th><th>Charges</th><th>Net P&L</th><th>R</th><th>Status</th>
          </tr></thead>
          <tbody>
            {rows.map(t => {
              const open = t.status === "OPEN";
              const { net, charges, gross } = moneyOf(t);
              const rMult = open
                ? (net != null && t.riskInr ? net / t.riskInr : null)
                : t.r_mult;
              const isGold = String(t.symbol).startsWith("PAXG") || String(t.symbol).startsWith("XAU");
              const book = t.book === "OBS" ? "15m OB-S gold" : t.book === "OBX" ? "1h OB-X gold" : t.book === "OB" ? "1h OB-retest" : t.side === "SHORT" ? "4h short" : t.tf === "1d" ? "1d trail" : isGold ? "4h gold" : `${t.tf} ST-trail`;
              return (
                <tr key={t.id} className={open ? "trade-open-row" : "trade-closed-row"} onClick={() => setDetail(t)} style={{ cursor: "pointer" }} title="Click for the full money breakdown">
                  <td style={{ whiteSpace: "nowrap" }}>{when(t.entry_time)}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{when(t.exit_time)}</td>
                  <td style={{ fontWeight: 600 }}>{t.symbol}{t.ref && <div style={{ fontSize: 9, fontWeight: 400, color: "var(--muted2)" }}>{t.ref}</div>}</td>
                  <td style={{ fontSize: 11 }}>{book}</td>
                  <td><span className={`signal-badge ${t.side === "SHORT" ? "badge-short" : "badge-long"}`}>{t.side === "SHORT" ? "SHORT" : "LONG"}</span></td>
                  <td style={{ whiteSpace: "normal", overflowWrap: "anywhere", maxWidth: 90 }}>{t.qty}{t.tierDone && (
                    <div className="trail-detail-label" style={{ fontSize: 10, marginTop: 2, fontWeight: 600 }}>
                      🏦 {Math.round(100 * (1 - (t.remainingQty ?? t.qty) / t.qty))}% @ {px(t.tierPrice, t.symbol)}
                      <span className="positive"> {rup(t.bookedPnl)}</span>
                      {open && t.remainingQty != null && <> · {t.remainingQty} runs</>}
                    </div>
                  )}</td>
                  <td>{px(t.entry, t.symbol)}</td>
                  <td className="sl">{px(t.stop, t.symbol)}{t.tierDone && <span style={{ fontSize: 9.5, color: "var(--muted2)" }}> BE</span>}</td>
                  <td>{t.target ? <span className="positive">{px(t.target, t.symbol)}</span> : <span style={{ color: "var(--muted2)" }}>{t.stArmed ? "ST trail ↑" : "ST trail"}</span>}</td>
                  <td className="premium-cell">{open ? <span style={{ color: "var(--muted2)" }}>—</span> : px(t.exit_price, t.symbol)}</td>
                  <td className="premium-cell">{(() => {
                    const cmp = st.livePrices?.[t.symbol] ?? (open ? t.lastPrice : null);
                    return cmp != null ? <span className="live-premium">{px(cmp, t.symbol)}</span> : <span style={{ color: "var(--muted2)" }}>—</span>;
                  })()}</td>
                  <td className="premium-cell">{inr(capitalOf(t))}</td>
                  <td className="premium-cell">
                    <b>{inr(marginOf(t))}</b>
                    <div style={{ fontSize: 9.5, color: "var(--muted2)" }}>{levOf(t) ? `@${effLevOf(t)}x · max ${levOf(t)}x` : `@${planLev}x`}</div>
                  </td>
                  <td className="premium-cell">{inr(t.riskInr)}</td>
                  <td className={gross != null ? (gross >= 0 ? "positive" : "negative") : ""}>{gross != null ? rup(gross) : "-"}</td>
                  <td className="premium-cell">{charges ? `₹${Math.round(charges).toLocaleString("en-IN")}${open ? "*" : ""}` : "-"}</td>
                  <td className={net != null ? (net >= 0 ? "positive" : "negative") : ""} style={{ fontWeight: 700 }}>
                    {open
                      ? (net != null ? <span className="running-pnl">{rup(net)} <span className="pulse-dot" style={{ display: "inline-block" }}></span></span> : "…")
                      : rup(net)}
                  </td>
                  <td className={(rMult || 0) >= 0 ? "positive" : "negative"}>{rMult != null ? `${rMult >= 0 ? "+" : ""}${Number(rMult).toFixed(2)}R` : "-"}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <span className={`status-badge status-${open ? "open" : "closed"}`}>{t.status}</span>
                    {!open && t.exit_reason && <span className="exit-reason-text"> ({t.exit_reason})</span>}
                    {open && (() => { const s = stageOf(t); return s ? (
                      <div style={{ fontSize: 10, marginTop: 2, fontWeight: 600 }} title={s.note}>
                        {s.icon} {s.label}
                        <div className="stage-note" style={{ fontSize: 9, color: "var(--muted2)", fontWeight: 400 }}>{s.note}</div>
                      </div>
                    ) : null; })()}
                  </td>
                </tr>
              );
            })}
            {!rows.length && <tr><td colSpan={19} style={{ color: "var(--muted2)" }}>{allRows.length ? "No trades match the current filters." : "No trades yet — entries only exist while the daily trend filter is ON for a symbol."}</td></tr>}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 10, color: "var(--muted2)", marginTop: 4 }}>
        Capital = notional position value in ₹ (quantity × entry price); the margin Shark would actually block is Capital ÷ your leverage setting (see the drawer for 5x/10x/25x). Risk = the ₹ lost if the SL is hit — identical on every trade by design, so rows compare directly in R. *Charges on OPEN rows include the estimated exit fee so Net = Gross − Charges holds live.
      </div>

      {detail && <MoneyDrawer t={detail} charges={chargesOf(detail)} live={livePnl(detail)} lev={levOf(detail)} cfg={st.config} params={st.params} onClose={() => setDetail(null)} />}

      {st.guard && (st.guard.armInr > 0) && (
        <div style={{ fontSize: 11, color: "var(--muted2)", marginTop: 8 }}>
          🛡️ Giveback guard: arms at ₹{Number(st.guard.armInr).toLocaleString("en-IN")} open P&L, banks all in-profit trades on a {Math.round((st.guard.givebackPct || 0.4) * 100)}% retreat from peak (losers keep their stops).
          {st.guard.peak > 0
            ? <> Armed — peak ₹{Math.round(st.guard.peak).toLocaleString("en-IN")}, trigger below ₹{Math.round(st.guard.peak * (1 - (st.guard.givebackPct || 0.4))).toLocaleString("en-IN")}.</>
            : <> Not armed (open P&L below the arm level).</>}
        </div>
      )}
      <div style={{ fontSize: 10, color: "var(--faint2)", marginTop: 8, lineHeight: 1.6 }}>
        Entries (both books): EMA50&gt;EMA200 &amp; close&gt;EMA50 · pullback touched EMA20 then closed back above · SL = 3-bar low − 0.5×ATR · max hold {st.params?.maxHoldDays}d · fees {(st.params?.feePerSide * 100).toFixed(3)}%/side ({((st.params?.takerFeePct ?? 0.0004) * 100).toFixed(3)}% Shark taker + 18% GST, per the venue fee page).
        1d book: full position, {st.params?.d1TrailBars ?? 20}-day-low structural trail — the stop only ratchets up to the lowest low of the last {st.params?.d1TrailBars ?? 20} closed daily bars; no tier, no fixed target; milestone lock — position tags +{st.params?.lockTagR ?? 5}R → floor rises to +{st.params?.lockFloorR ?? 3}R. (Replaced tier-50%-at-+0.65%→BE-runner-hunting-10R on 29 Sep 2026: 5.6y exact-rules replay put every tiered variant below its untiered twin — tier+10R +5.7R vs no-tier 20d-low trail +23.5R on the majors — and the forward book confirmed the bleed: −1.9R of BE-scratches with ZERO 10R targets ever hit.) 4h book: Supertrend({st.params?.stPeriod ?? 10},{st.params?.stMult ?? 3}) arm-then-trail — structural SL until ST turns up on a closed 4h bar, then stop = max(stop, ST line); up→down flip exits at market; no fixed target (honest replay: +147R vs +130R fixed-10R over 3y, positive every year &amp; symbol). Gold (PAXG/XAU): 4h only, fixed 10R kept — its smooth grinds reach 10R and hate trails (3y avgR +1.55, +58.8R; trail was 40% worse).
        SHORT book: 4h mirror entries (rally to EMA20 rejected in a 4h downtrend), fixed 10R, ONLY while the daily trend is down; shorts receive funding. Backtest: +31R in 2026 while longs idled; −3/−11R in bull years — the forward test judges it. Sizing: VALIDATION MODE — qty = risk ÷ SL-distance so every trade risks the same ₹ (1R); no account/margin/leverage limits, nothing skipped for budget (the index-tab fixed-lots analogue). Judge books by total R.
        Funding: charged at Shark's exact stamps (01:30/09:30/17:30 IST), rate modeled at {((st.config?.funding8hPct ?? 0.0001) * 100).toFixed(3)}%/interval on the remaining notional (longs pay when positive; the live rate isn't on the public API — read it in the app and adjust the config knob).
      </div>
    </div>
  );
}

/* the signal rationale — which book, which rule, and the numbers that fired it */
function WhyBlock({ t }) {
  const isShort = t.side === "SHORT";
  const isGold = String(t.symbol).startsWith("PAXG") || String(t.symbol).startsWith("XAU");
  const book = isShort ? "Bear-regime SHORT book (4h)"
    : t.tf === "1d" ? "1d trail book (20-day-low)" : isGold ? "Gold book (4h, fixed 10R)" : "4h Supertrend-trail book";
  const rule = isShort
    ? "4h downtrend (EMA50 < EMA200, close < EMA50) AND the daily regime is also down; price rallied to the 4h EMA20 and the candle closed back below it — a rejected rally, shorted on the close."
    : `${t.tf} uptrend (EMA50 > EMA200, close > EMA50); price pulled back to touch the EMA20 (previous close at/below it) and this candle closed back above — a reclaimed pullback, bought on the close.`;
  const c = t.signalCtx;
  const px2 = (v) => v == null ? "—" : `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: v > 1000 ? 0 : 2 })}`;
  const sigTime = t.sig_bar ? new Date(t.sig_bar).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }) : "—";
  return (
    <div style={{ fontSize: 12, lineHeight: 1.55 }}>
      <div style={{ marginBottom: 4 }}><b>{book}</b> · signal candle closed {sigTime}</div>
      <div style={{ color: "var(--muted2)", marginBottom: 6 }}>{rule}</div>
      {c ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 14px", fontSize: 11.5 }}>
          <span>prev close <b>{px2(c.prevClose)}</b></span>
          <span>EMA20 <b>{px2(c.e20)}</b></span>
          <span>EMA50 <b>{px2(c.e50)}</b></span>
          <span>EMA200 <b>{px2(c.e200)}</b></span>
          <span>{isShort ? "swing high" : "swing low"} (3-bar) <b>{px2(c.swing)}</b></span>
          <span>ATR14 <b>{px2(c.atr)}</b></span>
          <span style={{ color: "var(--muted2)" }}>SL = swing {isShort ? "+" : "−"} 0.5×ATR = {px2(c.swing + (isShort ? 0.5 : -0.5) * c.atr)}</span>
        </div>
      ) : (
        <div style={{ fontSize: 10.5, color: "var(--faint2)" }}>
          (Opened before signal-context recording — the numbers above weren't stored; the rule and signal-candle time still apply. New trades show EMA20/50/200, the swing level and the ATR that built the stop.)
        </div>
      )}
    </div>
  );
}
/* full money breakdown for one trade — capital, commissions, funding, P&L
   (exported: CryptoAnalytics reuses it for its trade drill-down).
   offsetRight: when set (e.g. "860px"), the drawer docks LEFT of another
   drawer at that width — no backdrop, so the list behind stays clickable. */
export function MoneyDrawer({ t, charges, live, lev, cfg, params, onClose, offsetRight }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const open = t.status === "OPEN";
  const rem = t.remainingQty ?? t.qty;
  const fx = t.fxEntry || 1;
  const notional = t.notional || t.entry * t.qty * fx;   // INR
  const qp = (v) => px(v, t.symbol);
  const exitPx = open ? t.lastPrice : t.exit_price;
  const sign = t.side === "SHORT" ? -1 : 1;
  const grossTier = t.tierPrice ? (t.tierPrice - t.entry) * (t.qty - rem) * sign * fx : 0;
  const grossRunner = exitPx != null ? (exitPx - t.entry) * rem * sign * fx : null;
  const gross = grossRunner != null ? grossTier + grossRunner : null;
  const net = open ? live : t.pnl_inr;
  const Row = ({ l, v, cls, strong, indent }) => (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", fontSize: 12.5, paddingLeft: indent ? 14 : 0 }}>
      <span style={{ color: "var(--muted2)" }}>{l}</span>
      <span className={cls} style={{ fontWeight: strong ? 700 : 500 }}>{v}</span>
    </div>
  );
  const Sep = ({ label }) => <div style={{ borderTop: "1px solid var(--panel3)", margin: "8px 0 4px", paddingTop: 4, fontSize: 10.5, fontWeight: 700, color: "var(--muted2)", letterSpacing: 0.5 }}>{label}</div>;
  return (
    // docked mode (offsetRight set): sits LEFT of the drawer that opened it,
    // no backdrop — the list behind stays clickable and each row click just
    // swaps the trade shown here. Otherwise: classic overlay with backdrop.
    <div onClick={offsetRight ? undefined : onClose} style={offsetRight
      ? { position: "fixed", top: 0, right: `min(${offsetRight}, calc(100vw - min(420px, 92vw)))`, height: "100vh", zIndex: 1300, display: "flex", boxShadow: "-8px 0 30px rgba(0,0,0,0.55)" }
      : { position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 1000, display: "flex", justifyContent: "flex-end" }}>
      <div onClick={e => e.stopPropagation()} style={{ width: "min(420px, 92vw)", height: "100%", overflowY: "auto", background: "var(--panel2, #1b1f27)", padding: "16px 18px", borderLeft: "1px solid var(--panel3)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <h3 style={{ margin: 0 }}>{t.ref ? `${t.ref} · ` : ""}{t.symbol} <span style={{ fontSize: 11, color: "var(--muted2)", fontWeight: 400 }}>{open ? "OPEN" : t.exit_reason}</span></h3>
          <button className="btn btn-sm btn-secondary" onClick={onClose}>✕</button>
        </div>

        <Sep label="WHY THIS TRADE" />
        <WhyBlock t={t} />

        <Sep label="POSITION" />
        <Row l="Risk at SL (= 1R)" v={`−₹${Math.round(t.riskInr || 0).toLocaleString("en-IN")}`} cls="negative" strong />
        <Row l="Quantity" v={`${t.qty} (runner ${rem})`} />
        <Row l="Capital (notional at entry)" v={`₹${Math.round(notional).toLocaleString("en-IN")} (${(notional / (t.riskInr || 1)).toFixed(1)}× the risked amount)`} />
        {t.quote === "USDT" && <Row l="Quoted in USDT · USDT/INR at entry" v={`${t.fxEntry}`} indent />}
        <Row l="Stop distance" v={`${(100 * Math.abs(t.entry - (t.signalCtx ? (t.side === "SHORT" ? t.signalCtx.swing + 0.5 * t.signalCtx.atr : t.signalCtx.swing - 0.5 * t.signalCtx.atr) : t.stop)) / t.entry).toFixed(2)}% from entry`} />
        <Row l="Margin this would block at Shark" v={`5x: ₹${Math.round(notional / 5).toLocaleString("en-IN")} · 10x: ₹${Math.round(notional / 10).toLocaleString("en-IN")} · 25x: ₹${Math.round(notional / 25).toLocaleString("en-IN")}`} indent />
        {lev && <Row l={`Shark max leverage for ${t.symbol}`} v={`${lev}x → min margin ₹${Math.round(notional / lev).toLocaleString("en-IN")}`} indent />}
        {lev && (() => { const ideal = notional / (1.5 * (t.riskInr || 10000)); const pl = Math.max(1, Math.floor(Math.min(lev, ideal))); return (
          <Row l="YOUR plan (stop-derived: liq ≥1.5× beyond SL)" v={`${pl}x → margin ₹${Math.round(notional / pl).toLocaleString("en-IN")}`} indent cls="positive" />
        ); })()}
        <div style={{ fontSize: 10, color: "var(--faint2)", margin: "2px 0 4px" }}>
          Leverage isn't in the order — it's your account's per-contract setting at Shark; quantity is fixed by risk, so your leverage setting only changes the margin blocked, not the P&L.
        </div>

        <Sep label="LIFECYCLE" />
        <Row l={`Entry · ${new Date(t.entry_time).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false })}`} v={qp(t.entry)} />
        {t.tierDone && <Row l={`Banked 🏦 ${Math.round(100 * (1 - rem / t.qty))}% · ${t.tierTime ? new Date(t.tierTime).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }) : "—"}`} v={t.tierPrice ? qp(t.tierPrice) : "+0.65%"} cls="positive" />}
        {t.bank2Done && <Row l={`Bank2 🏦 +${Math.round(100 * (t.qty && t.bank2Price ? (1 - (t.remainingQty ?? t.qty) / t.qty) : 0)) - 25}% · ${t.bank2Time ? new Date(t.bank2Time).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }) : ""}`} v={t.bank2Price ? qp(t.bank2Price) : "+5R"} cls="positive" />}
        <Row l="SL now" v={`${qp(t.stop)}${t.tierDone ? " (protected)" : ""}`} />
        <Row l="Target (10R)" v={t.target ? qp(t.target) : "ST trail"} />
        {!open && <Row l={`Exit · ${new Date(t.exit_time).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false })}`} v={qp(t.exit_price)} />}
        {open && t.lastPrice != null && <Row l="LTP" v={qp(t.lastPrice)} />}

        <Sep label="CHARGES — ITEMIZED" />
        {(() => { const f = ((params?.takerFeePct ?? 0.0004) * 100).toFixed(3); const g = ((params?.feeGstPct ?? 0.18) * 100).toFixed(0); const lbl = `${f}% taker + ${g}% GST`; return <>
        <Row l={`Buy leg: ₹${Math.round(t.qty * t.entry * fx).toLocaleString("en-IN")} × ${lbl}`} v={`₹${(t.entryFee || 0).toLocaleString("en-IN")}`} indent />
        {t.tierDone && <Row l={`Tier sell: ₹${Math.round((t.qty - rem) * (t.tierPrice || 0) * fx).toLocaleString("en-IN")} × ${lbl}`} v={`₹${(t.tierFee || 0).toLocaleString("en-IN")}`} indent />}
        {!open && <Row l={`Exit sell: ₹${Math.round(rem * (t.exit_price || 0) * fx).toLocaleString("en-IN")} × ${lbl}`} v={`₹${(t.exitFee || 0).toLocaleString("en-IN")}`} indent />}
        </>; })()}
        {(() => {
          const rate = (cfg?.funding8hPct ?? 0.0001) * 100;
          const start = Date.parse(t.entry_time); const end = open ? Date.now() : Date.parse(t.exit_time);
          const hrs = Math.max(0, (end - start) / 3600000);
          const remNotional = (t.lastPrice || t.exit_price || t.entry) * rem;
          const perDay = remNotional * (cfg?.funding8hPct ?? 0.0001) * 3;
          const short = t.side === "SHORT";
          return <>
            <Row l={`Funding: ${rate.toFixed(3)}%/8h ${short ? "RECEIVED (short)" : "paid (long)"} · held ${hrs < 48 ? hrs.toFixed(0) + "h" : (hrs / 24).toFixed(1) + "d"} (${Math.floor(hrs / 8)} intervals)`} v={`${(t.fundingFee || 0) < 0 ? "−" : ""}₹${Math.abs(t.fundingFee || 0).toLocaleString("en-IN")}`} indent cls={short ? "positive" : undefined} />
            {open && <Row l="Funding run-rate on remaining notional" v={`≈ ₹${Math.round(perDay).toLocaleString("en-IN")}/day`} indent />}
          </>;
        })()}
        <Row l="Total charges" v={`₹${Math.round(charges).toLocaleString("en-IN")}`} strong cls="negative" />
        <Row l="Charges as share of risk (1R)" v={`${(100 * charges / (t.riskInr || 1)).toFixed(1)}% of ₹${Math.round(t.riskInr || 0).toLocaleString("en-IN")}`} indent />
        <Row l="Price move needed to cover charges" v={`${(100 * charges / ((t.lastPrice || t.exit_price || t.entry) * rem || 1)).toFixed(3)}% on the runner`} indent />

        <Sep label="P&L" />
        {t.tierDone && <Row l="Tier leg banked (net of its fee)" v={`${(t.bookedPnl || 0) >= 0 ? "+" : "-"}₹${Math.abs(Math.round(t.bookedPnl || 0)).toLocaleString("en-IN")}`} cls="positive" indent />}
        {gross != null && <Row l="Gross P&L (both legs)" v={`${gross >= 0 ? "+" : "-"}₹${Math.abs(Math.round(gross)).toLocaleString("en-IN")}`} indent />}
        <Row l={open ? "Live net P&L (after all charges)" : "Net P&L (after all charges)"} v={net == null ? "…" : `${net >= 0 ? "+" : "-"}₹${Math.abs(Math.round(net)).toLocaleString("en-IN")}`} cls={net != null ? (net >= 0 ? "positive" : "negative") : ""} strong />
        {t.r_mult != null && <Row l="R multiple" v={`${t.r_mult >= 0 ? "+" : ""}${t.r_mult}R`} cls={t.r_mult >= 0 ? "positive" : "negative"} />}
        <div style={{ fontSize: 10, color: "var(--faint2)", marginTop: 10, lineHeight: 1.5 }}>
          Fixed-risk validation sizing (no budget constraint) · funding modeled (venue publishes no public rate){t.note === "DUMMY" ? " · DUMMY demo trade" : ""}
        </div>
      </div>
    </div>
  );
}
